
import { useState, useEffect, useCallback, FC, useRef } from "react";
import { CorporateTheme } from "../../utils/themes";
import debounce from "lodash/debounce";
import styled, { keyframes } from "styled-components";
import DateRangeFilter from "./DateRangeFilter";
import Filters from "./Filters";
import ExpandableSearch from "./ExpandableSearch";
import FilterIcon from "../../assets/icons/FilterIcon";
import Actions from "./Actions";
import Pagination from "./Pagination";
import DataTable from "./DataTable";
import { Column, ImportRecord, TableConfig } from "../../types/types";
import { DropResult } from "@hello-pangea/dnd";
import AdvanceSearchDropDown from "./AdvanceSearchDropDown";
import ColumnIdentifier from "./ColumnIdentifier";
import ModalWrapper from "../ModelWrapper";
import RolePermissionButton, { ActionButton } from "./RolePermissionButton";
import { useTheme } from "../../middleware/ThemeContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "../toast/ToastContext";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import ColumnIdentifierIcon from "../../assets/icons/ColumnIdentifierIcon";
import ExportIcon from "../../assets/icons/ExportIcon";
import MergeIcon from "../../assets/icons/MergeIcon";
import FileHeader from "../../features/imports/FileHeader";
import { FilterCountBadge, PaginationWrapper } from "./AMSListing";
import { StyledHr } from "../../features/contacts/ContactOverview/ContactOverview";
import { StyledHorizontalLine } from "../StyledComponents/Styledhr";
import SyncIcon from "../../assets/icons/SyncIcon";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const Container = styled('div')(({ theme }) => ({
  background: theme.colors.contentBackground,
  borderRadius: CorporateTheme.borderRadius,
  padding: CorporateTheme.spacing.md,
  position: 'relative',
}));
interface HeaderContainerProps {
  routeKey?: string;
}

export const HeaderContainer = styled('div')<HeaderContainerProps>(({ theme, routeKey }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: CorporateTheme.spacing.md,
  alignItems: 'flex-start',
  // borderBottom: routeKey === 'records' ? 'none' : '1px solid ' + theme.CorporateTheme.colors.border,

  '@media (min-width: 768px)': {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

export const TitleGroup = styled.div`
  flex: 1;
`;

export const TitleWrapper = styled.div<{ additionalTitle?: string }>`
  display: flex;
  align-items: baseline;
  gap: ${CorporateTheme.spacing.sm};
  margin-top: ${({ additionalTitle }) => additionalTitle ? '12px' : 0};
`;

export const TitleCount = styled('span')(({ theme }) => ({
  fontSize: '20px',
  fontFamily: "Lato, sans-serif",
  color: theme.CorporateTheme.colors.count,
  fontWeight: '400',
}));

export const Title = styled('h1')(({ theme }) => ({
  fontSize: '20px',
  fontWeight: '700',
  color: theme.CorporateTheme.colors.primary,
  margin: '0',
}));

export const Subtitle = styled('p')(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.CorporateTheme.colors.textSecondary,
  margin: CorporateTheme.spacing.xs + '0' + CorporateTheme.spacing.sm + '0',
}));

export const ControlsContainer = styled.div`
  display: flex;
  gap: ${CorporateTheme.spacing.md};
  flex-wrap: nowrap;
  width: 100%;
  justify-content: flex-end;
  @media (min-width: 768px) {
    width: auto;
  }
`;

export const FilterButton = styled('button')<{ $active?: boolean, disabled?: boolean }>(
  ({ theme, $active, disabled }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: CorporateTheme.spacing.sm,
    border: 'none',
    background: $active
      ? theme.CorporateTheme.colors.filterBG
      : 'none',
    padding: `${CorporateTheme.spacing.sm} ${CorporateTheme.spacing.sm}`,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? '0.5' : '1',
    color: theme.CorporateTheme.colors.textSecondary,
    fontSize: '14px',
    fontFamily: '"Raleway", sans-serif',
    fontWeight: 500,
    transition: 'all 0.2s',
    borderRadius: CorporateTheme.borderRadius,

    '&:hover': {
      color: theme.CorporateTheme.colors.accent,
      background: $active
        ? theme.CorporateTheme.colors.filterBG
        : disabled ?
          'transparent'
          : 'rgba(0, 0, 0, 0.05)',
    },
  })
);

export const SelectButton = styled('button')<{ $active?: boolean }>(({ theme, $active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: CorporateTheme.spacing.sm,
  border: 'none',
  background: $active ? theme.CorporateTheme.colors.filterBG : 'none',
  padding: CorporateTheme.spacing.sm,
  cursor: 'pointer',
  color: theme.CorporateTheme.colors.textSecondary,
  fontSize: '14px',
  fontFamily: '"Raleway", sans-serif',
  fontWeight: 500,
  transition: 'all 0.2s',
  borderRadius: CorporateTheme.borderRadius,

  '&:hover': {
    color: theme.CorporateTheme.colors.accent,
    background: $active
      ? theme.CorporateTheme.colors.filterBG
      : 'rgba(0, 0, 0, 0.05)',
  },
}));

export const Divider = styled.div`
  width: 1px;
  height: 24px;
  background-color: #dddddd;
  align-self: center;
`;
interface TableProps {
  title: string;
  subtitle: string;
  columns: Column[];
  data: any[];
  totalItems: number;
  loading: boolean;
  isFetching?: boolean;
  isFetchComplete?: boolean;
  noDataTitle?: string;
  noDataSubtitle?: string;
  routeKey: string;
  showAdvanceSearch?: boolean;
  showFilters?: boolean;
  showDateRangeFilter?: boolean;
  showActions?: boolean;
  noTableAction?: boolean;
  additionaltitle?: string;
  onAction: (action: "sync" | "view" | "edit" | "delete" | "share" | "duplicate" | "copy" | "revoke" | "condition" | "ignore" | "showAssignedContacts", id: string | number[], state?: string | (() => void)) => void;
  onFetchRequested?: (params: {
    page: number;
    perPage: number;
    sort?: { field: string; direction: "ASC" | "DESC" };
    query?: string;
    filters: Record<string, any>;
    visibleColumns: string[];
  }) => void;
  filterGroups?: {
    dataType?: string;
    key: string;
    label: string;
    options: { value: string; label: string }[];
  }[];
  setInitialFilters?: any;
  setSelectedRecordIds?: (ids: number[]) => void;
  dynamicColumnsListing?: boolean;
  skeletonHeight?: string;
  onExport?: () => void;
  loadingExport?: boolean;
  ImportData?: ImportRecord;
  scope?: string,
  fileId?: string;
  containerHeight?: number,
  hideActionColumn?: boolean,
  refetchImportData?: () => void;
}

const AMSDynamicListing: FC<TableProps> = ({
  title,
  subtitle,
  noDataTitle,
  noDataSubtitle,
  columns,
  data,
  totalItems,
  loading,
  isFetching,
  isFetchComplete,
  onAction,
  onFetchRequested,
  setInitialFilters,
  filterGroups,
  routeKey,
  setSelectedRecordIds,
  showAdvanceSearch,
  showFilters,
  showDateRangeFilter,
  showActions,
  noTableAction,
  dynamicColumnsListing,
  skeletonHeight,
  additionaltitle,
  onExport,
  loadingExport = false,
  ImportData,
  scope,
  fileId,
  containerHeight,
  hideActionColumn,
  refetchImportData
}) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const showToast = useToast();
  let storageKey = scope ? `${scope}s` : routeKey
  let keys = storageKey === 'contacts' || storageKey === 'relationships' ? 'contacts' : storageKey
  // const STORAGE_KEY = `tableConfig_${keys}`;
  const shouldUseImportPrefix = routeKey === 'records' && scope;

  // const STORAGE_KEY = `tableConfig_${keys}`;
  const STORAGE_KEY = shouldUseImportPrefix
    ? `tableConfig_import_${keys}`
    : `tableConfig_${keys}`;


  const [tableConfig, setTableConfig] = useState<TableConfig>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && !shouldUseImportPrefix) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          return parsed as TableConfig;
        }
      }
    } catch (e) {
      // ignore parse errors and fall back to defaults
    }
    return {
      visibleColumns: [],
      dynamicColumns: [],
      page: 1,
      perPage: 10,
      sort: undefined,
      filters: {},
      query: "",
      columnOrder: [],
    };
  });

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isFiltersOpen, setisFiltersOpen] = useState(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;
  const [showCheckboxColumn, setShowCheckboxColumn] = useState(false);
  const [isColumnIdentifierModalOpen, setIsColumnIdentifierModalOpen] = useState(false);
  const [filtersContainerHeight, setFiltersContainerHeight] = useState<number>(0);

  // --- NEW: refs to manage page-adjust flow and prevent accidental resets ---
  const pendingAdjustedPageRef = useRef<number | null>(null);
  const suppressSetInitialFiltersRef = useRef(false);
  // track whether we've loaded initial config from localStorage to avoid
  // persisting the empty initial state and overwriting saved config
  const initialConfigLoadedRef = useRef(false);

  const hasAppliedFilters = Object.values(tableConfig.filters).some(
    (filterGroup) => Array.isArray(filterGroup) && filterGroup?.length > 0
  );
  const totalPages = Math.ceil(totalItems / tableConfig.perPage);
  const shouldPersistConfig = useCallback(() => {
    return ['contacts', 'organizations', 'records'].includes(routeKey);
  }, [routeKey]);



  useEffect(() => {


    if (!columns || columns.length === 0) return;

    const defaultConfig: TableConfig = {
      // default to first 5 columns only (if available)
      visibleColumns: columns.slice(0, 10).map(col => col.id),
      dynamicColumns: [],
      page: 1,
      perPage: 10,
      sort: undefined,
      filters: {},
      query: "",
      columnOrder: columns.slice(0, 10).map(col => col.id),
    };


    try {
      let parsedConfig = defaultConfig;
      if (shouldPersistConfig()) {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const savedConfig = JSON.parse(saved);
          if (savedConfig &&
            Array.isArray(savedConfig.visibleColumns) &&
            typeof savedConfig.page === 'number' &&
            typeof savedConfig.perPage === 'number'
          ) {
            // Intersect saved visibleColumns with current available columns to
            // avoid restoring invalid/removed column ids.
            const currentColumnIds = new Set(columns.map(c => c.id));
            const restoredVisible = Array.isArray(savedConfig.visibleColumns)
              ? savedConfig.visibleColumns.filter((c: string) => currentColumnIds.has(c))
              : [];

            const restoredColumnOrder = Array.isArray(savedConfig.columnOrder)
              ? savedConfig.columnOrder.filter((c: string) => currentColumnIds.has(c))
              : defaultConfig.columnOrder;

            parsedConfig = {
              ...defaultConfig,
              visibleColumns: restoredVisible.length > 0 ? restoredVisible : defaultConfig.visibleColumns,
              filters: savedConfig.filters || {},
              page: savedConfig.page,
              perPage: savedConfig.perPage,
              columnOrder: restoredColumnOrder.length > 0 ? restoredColumnOrder : defaultConfig.columnOrder,
            };
          }
        }
      }

      setTableConfig(parsedConfig);
      // mark that we've loaded initial config so persistence can start
      initialConfigLoadedRef.current = true;
      if (setInitialFilters) {
        setInitialFilters({
          page: parsedConfig.page,
          perPage: parsedConfig.perPage,
          sort: parsedConfig.sort,
          query: parsedConfig.query,
          filters: parsedConfig.filters,
          visibleColumns: parsedConfig.visibleColumns
        });
      }
    } catch {
      setTableConfig(defaultConfig);
    }
  }, [columns, routeKey, setInitialFilters, shouldPersistConfig]);

  // helper to build fetch config from current tableConfig (useful for immediate fetches)
  const buildFetchConfig = useCallback((overridePage?: number) => ({
    page: typeof overridePage === 'number' ? overridePage : tableConfig.page,
    perPage: tableConfig.perPage,
    sort: tableConfig.sort,
    query: tableConfig.query,
    filters: {
      ...tableConfig.filters,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
    },
    visibleColumns: tableConfig.visibleColumns,
    columnOrder: tableConfig.columnOrder
  }), [tableConfig, startDate, endDate]);

  const debouncedFetch = useCallback(
    debounce(
      (config: Parameters<NonNullable<typeof onFetchRequested>>[0]) =>
        (onFetchRequested ?? (() => { }))(config),
      300
    ),
    [onFetchRequested]
  );

  // Main fetch effect (triggered on tableConfig changes)
  useEffect(() => {
    const config = buildFetchConfig();

    // Only persist after we've loaded initial config from localStorage. This
    // prevents the initial empty state from overwriting saved settings on mount.
    if (shouldPersistConfig() && initialConfigLoadedRef.current) {
      // Persist the config (page here is the authoritative tableConfig.page)
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        visibleColumns: tableConfig.visibleColumns,
        filters: tableConfig.filters,
        page: tableConfig.page,
        perPage: tableConfig.perPage,
        columnOrder: tableConfig.columnOrder,
      }));
    }

    // Always do the fetch, but avoid calling setInitialFilters while we are in the middle
    // of a programmatic page adjustment — that prevents parent from resetting page to 1.
    debouncedFetch(config);

    if (setInitialFilters && !suppressSetInitialFiltersRef.current) {
      setInitialFilters(config);
    }
  }, [tableConfig, debouncedFetch, startDate, endDate, setInitialFilters, shouldPersistConfig, buildFetchConfig]);

  // --- NEW: if current page returned 0 results, attempt previous page ONLY once ---
  useEffect(() => {
    // conditions: finished fetching, no items, we have pages > 1, and we haven't already tried adjusting
    if (!loading && isFetchComplete && (data?.length ?? 0) === 0 && tableConfig.page > 1 && pendingAdjustedPageRef.current === null) {
      const prevPage = tableConfig.page - 1;

      // mark that we're attempting to adjust to prevPage (prevents re-entrancy)
      pendingAdjustedPageRef.current = prevPage;

      // suppress setInitialFilters briefly while we change page programmatically
      suppressSetInitialFiltersRef.current = true;

      // Update local state page immediately (this will also trigger the main fetch effect)
      setTableConfig((prev) => ({
        ...prev,
        page: prev.page - 1,
      }));

      // Trigger an immediate fetch for the previous page (bypass debounce) — parent may expect immediate action
      try {
        const prevConfig = buildFetchConfig(prevPage);
        (onFetchRequested ?? (() => { }))(prevConfig);
      } catch (err) {
        // swallow — onFetchRequested may be undefined or not return a promise
      }
    }
  }, [data, loading, isFetchComplete, tableConfig.page, buildFetchConfig, onFetchRequested]);

  // --- NEW: clear suppression + pending flag once the adjusted fetch finishes ---
  useEffect(() => {
    if (pendingAdjustedPageRef.current !== null) {
      // once fetch completes for the adjusted page, clear the flags so normal behaviour resumes
      if (!loading && isFetchComplete) {
        // if we received items for the adjusted page OR we've reached page 1 (no more prev pages), reset
        if ((data?.length ?? 0) > 0 || tableConfig.page <= (pendingAdjustedPageRef.current ?? 0)) {
          pendingAdjustedPageRef.current = null;
          suppressSetInitialFiltersRef.current = false;
        }
      }
    }
  }, [data, loading, isFetchComplete, tableConfig.page]);

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;

      const newColumnOrder = Array.from(tableConfig.columnOrder);
      const [removed] = newColumnOrder.splice(result.source.index, 1);
      newColumnOrder.splice(result.destination.index, 0, removed);

      setTableConfig((prev) => ({
        ...prev,
        columnOrder: newColumnOrder,
      }));
    },
    [tableConfig.columnOrder]
  );

  const handleSort = useCallback((field: string) => {
    setTableConfig((prev) => ({
      ...prev,
      sort: {
        field,
        direction: prev.sort?.direction === "ASC" ? "DESC" : "ASC",
      },
    }));
    setInitialFilters &&
      setInitialFilters((prev: any) => {
        return {
          ...prev,
          sort: {
            field,
            direction: prev.sort?.direction === "ASC" ? "DESC" : "ASC",
          },
        };
      });
  }, []);

  const debouncedSetQuery = useCallback(
    debounce((value: string) => {
      setInitialFilters((prev: any) => ({
        ...prev,
        query: value,
      }));
    }, 300),
    []
  );

  const handleSelectAll = useCallback((ids: number[]) => {
    setSelectedIds(ids);
    if (setSelectedRecordIds) {
      setSelectedRecordIds(ids);
    }
  }, [setSelectedRecordIds]);

  const handleDeleteSelected = useCallback(() => {
    // if (setSelectedRecordIds) {
    //   setSelectedRecordIds(selectedIds);
    try {
      onAction && onAction("delete", selectedIds, () => setSelectedIds([]));
    } catch (error) {
      console.error(error);
    }
    // finally {
    //   setSelectedIds([]);
    // }
    // }

  }, [selectedIds, onAction]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const handleSelectRow = useCallback((id: number) => {
    setSelectedIds(prev => {
      const newIds = prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id];

      if (setSelectedRecordIds) {
        setSelectedRecordIds(newIds);
      }
      return newIds;
    });
  }, [setSelectedRecordIds]);

  const handleToggleColumnIdentifier = () => {
    setIsColumnIdentifierModalOpen(true);
  };

  useEffect(() => {
  }, [selectedIds]);
  const handleSaveColumns = (
    dynamicColumns: Column[],
    selectedColumns: string[]
  ) => {
    const newVisibleColumns = selectedColumns?.length > 0 ? selectedColumns : [];
    // Ensure saved visible columns are valid with respect to current columns
    const currentColumnIds = new Set(columns.map(c => c.id));
    const filteredVisible = newVisibleColumns.filter(col => currentColumnIds.has(col));
    const newColumnOrder = newVisibleColumns;
    setTableConfig((prev) => ({
      ...prev,
      dynamicColumns: dynamicColumns,
      visibleColumns: filteredVisible.filter((col) => col !== "id"),
      columnOrder: newColumnOrder.filter(col => currentColumnIds.has(col)),
      page: 1,
    }));
    setIsColumnIdentifierModalOpen(false);
  };

  const handleMerge = () => {
    if (selectedIds && selectedIds.length >= 2) {
      const queryString = selectedIds.join(',');
      if (routeKey == 'contacts') {
        navigate(`/home/contacts/merge?ids=${queryString}`);
      } else if (routeKey == 'organizations') {
        navigate(`/home/organizations/merge?ids=${queryString}`);
      }
    } else {
      if (routeKey == 'contacts') {
        showToast('Error', 'Select atleast 2 or more contacts to merge.', 'error');
      } else if (routeKey == 'organizations') {
        showToast('Error', 'Select atleast 2 or more organizations to merge.', 'error');
      }
    }
  }

  const fileData = {
    importName: ImportData?.fileName || "-",
    fileName: ImportData?.meta?.originalFileName || "-",
    status: ImportData?.statusId || 0,
    importedBy: ImportData?.account?.firstName + ' ' + ImportData?.account?.lastName,
    errorCounts: ImportData?.meta?.errorCount || 0,
    duplicates: ImportData?.meta?.duplicateCount || 0,
  };


  const labels = {
    importName: "Import Name",
    fileName: "File Name",
    status: "Status",
    importedBy: "Imported By",
    errorCounts: "Error Counts",
    duplicates: "Duplicates",
  };


  const formatter = new Intl.NumberFormat('en-US');
  return (
    <Container theme={theme} style={{ margin: '12px' }}>
      <HeaderContainer theme={theme} routeKey={routeKey}>
        <TitleGroup>
          <TitleWrapper >
            <Title theme={theme}>{title}</Title>
            {additionaltitle ? null : <TitleCount theme={theme}>({formatter.format(totalItems)})</TitleCount>}
          </TitleWrapper>
          <Subtitle theme={theme}>{subtitle}</Subtitle>
        </TitleGroup>

        <ControlsContainer>
          {routeKey === "records" &&
            ImportData?.statusId === 56 &&
            <ActionButton theme={theme} onClick={refetchImportData}>
              <SyncIcon color={theme.CorporateTheme.colors.textSecondary} /> Refresh
            </ActionButton>
          }
          <ExpandableSearch
            disabled={loading || isFetching}
            value={tableConfig.query}
            onChange={(e: any) => {
              setTableConfig((prev) => ({ ...prev, query: e.target.value }));

              setInitialFilters && debouncedSetQuery(e.target.value);
            }}
          />
          {title.includes("Contact Roles") && (
            <RolePermissionButton />

          )}
          {showAdvanceSearch && <> <AdvanceSearchDropDown /> <Divider /></>}
          {showFilters && (
            <FilterButton
              disabled={loading || isFetching || data?.length === 0}
              theme={theme}
              onClick={() => { if (!loading || !isFetching || data?.length !== 0) setisFiltersOpen(!isFiltersOpen) }}
              $active={
                isFiltersOpen ||
                hasAppliedFilters ||
                !!localStorage.getItem(`persistedDateRange_${routeKey}`)
              }
            >
              <FilterIcon color={theme.CorporateTheme.colors.textSecondary} />
              {!hasAppliedFilters ? 'Filters' : 'Applied Filters'}

              {hasAppliedFilters && (
                <FilterCountBadge>
                  {Object.values(tableConfig.filters).reduce((count, values) => {
                    if (!values) return count;
                    if (Array.isArray(values) && values.length > 0) {
                      return count + 1; // count whole group once
                    }
                    if (typeof values === "string" && (values as string).trim() !== "") {
                      return count + 1; // count string group
                    }
                    return count;
                  }, 0)}
                </FilterCountBadge>
              )}

            </FilterButton>
          )}



          {showActions && (
            <Actions
              disabled={loading || isFetching || data?.length === 0}
              actions={[
                {
                  type: "deleteSelected",
                  label: "Delete Selected",
                  icon: <DeleteIcon />,
                  onClick: handleDeleteSelected,
                  visible: (() => {
                    // Only show Delete Selected for Contacts or Organization tabs
                    const allowedTabs = ["contacts", "organizations"];
                    if (!allowedTabs.includes(routeKey)) return false;

                    // Only show if the checkbox column is visible
                    if (!showCheckboxColumn) return false;

                    // You could add more conditions if needed
                    return true;
                  })(),
                },
                {
                  type: "toggleColumnIdentifier",
                  label: "Column Identifier",
                  icon: (
                    <ColumnIdentifierIcon color={theme.CorporateTheme.colors.textSecondary} />
                  ),
                  onClick: handleToggleColumnIdentifier,
                  visible: !!title && !title.includes("Form") && !title.includes("Field"),
                },
                {
                  type: "export",
                  label: "Export Data",
                  icon: <ExportIcon color={theme.CorporateTheme.colors.textSecondary} />,
                  onClick: onExport ?? (() => { }),
                  visible: true,
                  isLoading: loadingExport,
                },
                {
                  type: "toggleSelect",
                  label: showCheckboxColumn ? "Hide Selection" : "Show Selection",
                  icon: (
                    <ColumnIdentifierIcon color={theme.CorporateTheme.colors.textSecondary} />
                  ),
                  onClick: () => setShowCheckboxColumn(!showCheckboxColumn),
                  visible: routeKey !== "records" && true,
                },
                {
                  type: "mergeContacts",
                  label: "Merge Contacts",
                  icon: <MergeIcon color={theme.CorporateTheme.colors.textSecondary} />,
                  onClick: () => handleMerge(),
                  visible: routeKey !== "records" && title?.includes("Contacts Listing"),
                },
                {
                  type: "mergeOrganizations",
                  label: "Merge Organizations",
                  icon: <MergeIcon color={theme.CorporateTheme.colors.textSecondary} />,
                  onClick: () => handleMerge(),
                  visible: routeKey !== "records" && title?.includes("Organizations Listing"),
                },
              ]}
            />
          )}

        </ControlsContainer>
      </HeaderContainer>
      {routeKey === "records" ? <FileHeader data={fileData} labels={labels} fileId={fileId} loading={loading || isFetching} totalRecords={ImportData?.meta?.totalRecords} totalProcessed={ImportData?.meta?.totalProcessed} /> : null}

      <Filters
        setShowFilters={setisFiltersOpen}
        setFiltersContainerHeight={setFiltersContainerHeight}
        isFiltersOpen={isFiltersOpen}
        filterGroups={filterGroups ?? []}
        filters={tableConfig.filters}
        routeKey={routeKey}
        onFilterChange={(newFilters) => setTableConfig(prev => ({
          ...prev,
          filters: newFilters,
          page: 1,
        }))}
      />

      {routeKey === 'records' ? null : <StyledHorizontalLine theme={theme} />}
      <DataTable
        forms={title.includes("Form")}
        isFields={title.includes("Field")}
        components={title.includes("Component")}
        title={title}
        showCheckboxColumn={showCheckboxColumn}
        columns={columns}
        routeKey={routeKey}
        data={data}
        columnOrder={tableConfig.columnOrder}
        visibleColumns={tableConfig.visibleColumns}
        sort={tableConfig.sort}
        noDataTitle={noDataTitle}
        noDataSubtitle={noDataSubtitle}
        onDragEnd={handleDragEnd}
        onSort={handleSort}
        loading={loading}
        isFetching={isFetching}
        isFetchComplete={isFetchComplete}
        perPage={tableConfig.perPage}
        noAction={noTableAction}
        selectedIds={selectedIds}
        onSelectAll={handleSelectAll}
        onSelectRow={handleSelectRow}
        onAction={onAction}
        dynamicColumnsListing={dynamicColumnsListing}
        skeletonHeight={skeletonHeight}
        containerHeight={containerHeight}
        filterHeight={filtersContainerHeight}
        hideActionColumn={hideActionColumn}
      />
      {!loading && !isFetching && (
        <PaginationWrapper theme={theme}>
          {totalItems !== 0 ?
            <Pagination
              currentPage={tableConfig.page}
              totalItems={totalItems}
              perPage={tableConfig.perPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                setTableConfig((prev) => ({ ...prev, page }));
                if (routeKey === 'records' && refetchImportData && ImportData?.statusId === 56) {
                  refetchImportData();
                }
              }}
              onLimitChange={(newLimit) =>
                setTableConfig((prev) => ({
                  ...prev,
                  perPage: newLimit,
                  page: 1,
                }))
              }
            />
            : null}
        </PaginationWrapper>
      )}
      {isColumnIdentifierModalOpen && (
        <ModalWrapper onClose={() => setIsColumnIdentifierModalOpen(false)}>
          <ColumnIdentifier
            entityType={storageKey}
            onClose={() => setIsColumnIdentifierModalOpen(false)}
            onSave={handleSaveColumns}
            availableColumns={[]}
            initialSelectedColumns={tableConfig.visibleColumns
              || []
            }
          />
        </ModalWrapper>
      )}
    </Container>
  );
};


export default AMSDynamicListing;
